import React, { useState, useEffect, useRef } from 'react';
import { 
    Card, 
    Input, 
    Button, 
    List, 
    Avatar, 
    Typography, 
    Space, 
    Badge, 
    Tooltip,
    message,
    Spin,
    Empty,
    Tag,
    Divider
} from 'antd';
import { 
    SendOutlined, 
    RobotOutlined, 
    UserOutlined, 
    MessageOutlined,
    CustomerServiceOutlined,
    CloseOutlined,
    MinusOutlined,
    FullscreenOutlined
} from '@ant-design/icons';
import aiCustomerSupport from '../../services/aiCustomerSupport';

const { TextArea } = Input;
const { Text, Title } = Typography;

const AISupportChat = ({ 
    visible = true,
    onClose,
    onEscalate,
    className = "",
    position = "bottom-right" // bottom-right, bottom-left, top-right, top-left
}) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (visible && !isMinimized) {
            // Add welcome message
            const welcomeMessage = {
                id: Date.now(),
                type: 'ai',
                message: "Hello! I'm your AI assistant. I can help you with orders, menu information, delivery, payments, and account questions. What can I help you with today?",
                timestamp: new Date().toISOString(),
                analysis: { category: 'general', intent: 'greeting' }
            };
            setMessages([welcomeMessage]);
        }
    }, [visible, isMinimized]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (visible && !isMinimized && inputRef.current) {
            inputRef.current.focus();
        }
    }, [visible, isMinimized]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || loading) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            message: newMessage.trim(),
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setNewMessage('');
        setLoading(true);
        setIsTyping(true);

        try {
            // Process message with AI
            const response = await aiCustomerSupport.processQuery(userMessage.message, {
                user_id: 'current_user', // Replace with actual user ID
                session_id: Date.now().toString()
            });

            if (response.success) {
                const aiMessage = {
                    id: Date.now() + 1,
                    type: 'ai',
                    message: response.response,
                    timestamp: new Date().toISOString(),
                    analysis: response.analysis,
                    needsEscalation: response.needsEscalation
                };

                setMessages(prev => [...prev, aiMessage]);

                // Handle escalation
                if (response.needsEscalation && onEscalate) {
                    setTimeout(() => {
                        onEscalate(response);
                    }, 2000);
                }
            } else {
                const errorMessage = {
                    id: Date.now() + 1,
                    type: 'ai',
                    message: "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.",
                    timestamp: new Date().toISOString(),
                    isError: true
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        } catch (error) {
            console.error('AI support error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                type: 'ai',
                message: "I'm experiencing technical difficulties. Please try again or contact our support team directly.",
                timestamp: new Date().toISOString(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const getPositionClasses = () => {
        switch (position) {
            case 'bottom-left':
                return 'bottom-2 left-2 sm:bottom-4 sm:left-4';
            case 'top-right':
                return 'top-2 right-2 sm:top-4 sm:right-4';
            case 'top-left':
                return 'top-2 left-2 sm:top-4 sm:left-4';
            default:
                return 'bottom-2 right-2 sm:bottom-4 sm:right-4';
        }
    };

    const renderMessage = (message) => {
        const isUser = message.type === 'user';
        const isAI = message.type === 'ai';
        const isError = message.isError;

        return (
            <div
                key={message.id}
                className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
            >
                <div className={`flex items-start gap-2 max-w-xs ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                    <Avatar
                        size="small"
                        icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                        className={isUser ? 'bg-blue-500' : 'bg-green-500'}
                    />
                    <div className={`rounded-lg px-3 py-2 ${isUser ? 'bg-blue-500 text-white' : isError ? 'bg-red-50 border border-red-200' : 'bg-gray-100'}`}>
                        <Text className={isUser ? 'text-white' : isError ? 'text-red-700' : 'text-gray-800'}>
                            {message.message}
                        </Text>
                        {message.analysis && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {message.analysis.category && (
                                    <Tag size="small" color="blue">{message.analysis.category}</Tag>
                                )}
                                {message.analysis.urgency === 'high' && (
                                    <Tag size="small" color="red">Urgent</Tag>
                                )}
                            </div>
                        )}
                        {message.needsEscalation && (
                            <div className="mt-2">
                                <Tag color="orange" icon={<CustomerServiceOutlined />}>
                                    Connecting to human agent...
                                </Tag>
                            </div>
                        )}
                        <div className="text-xs mt-1 opacity-70">
                            {new Date(message.timestamp).toLocaleTimeString()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (!visible) return null;

    if (isMinimized) {
        return (
            <div className={`fixed ${getPositionClasses()} z-50`}>
                <Card
                    className="shadow-lg cursor-pointer"
                    bodyStyle={{ padding: '12px 16px' }}
                    onClick={() => setIsMinimized(false)}
                >
                    <div className="flex items-center gap-2">
                        <Avatar size="small" icon={<RobotOutlined />} className="bg-green-500" />
                        <Text strong>AI Support</Text>
                        <Badge count={messages.filter(m => m.type === 'user').length} size="small" />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className={`fixed ${getPositionClasses()} z-[9999] ${className}`}>
            <Card
                className="shadow-lg w-80 sm:w-80 md:w-80 lg:w-80 xl:w-80 max-w-[calc(100vw-2rem)]"
                title={
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Avatar size="small" icon={<RobotOutlined />} className="bg-green-500" />
                            <div>
                                <Title level={5} className="mb-0">AI Support</Title>
                                <Text className="text-xs text-gray-500">Online</Text>
                            </div>
                        </div>
                        <Space>
                            <Tooltip title="Minimize">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<MinusOutlined />}
                                    onClick={() => setIsMinimized(true)}
                                />
                            </Tooltip>
                            {onClose && (
                                <Tooltip title="Close">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseOutlined />}
                                        onClick={onClose}
                                    />
                                </Tooltip>
                            )}
                        </Space>
                    </div>
                }
                bodyStyle={{ padding: 0, height: '400px', maxHeight: 'calc(100vh - 8rem)', display: 'flex', flexDirection: 'column' }}
            >
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4">
                    {messages.length === 0 ? (
                        <Empty
                            description="Start a conversation"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        <div>
                            {messages.map(renderMessage)}
                            {isTyping && (
                                <div className="flex justify-start mb-4">
                                    <div className="flex items-start gap-2">
                                        <Avatar size="small" icon={<RobotOutlined />} className="bg-green-500" />
                                        <div className="bg-gray-100 rounded-lg px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <Spin size="small" />
                                                <Text className="text-gray-600 text-sm">AI is typing...</Text>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>

                <Divider className="my-0" />

                {/* Input Area */}
                <div className="p-4">
                    <div className="flex gap-2">
                        <TextArea
                            ref={inputRef}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message..."
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            disabled={loading}
                        />
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={handleSendMessage}
                            loading={loading}
                            disabled={!newMessage.trim()}
                        />
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                        Press Enter to send, Shift+Enter for new line
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default AISupportChat;
